import React from "react";
import { useIntl } from "react-intl";
import { styled } from "@mui/material";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Chip } from "../components";
import { stringify } from "components/EnVKV/helper";

interface ICard {
  data: any;
  onClickHandler?: any;
  paperProps?: any;
  vatExcluded?: boolean;
}

const defaultPicture = "https://cdn.carbando.eu/develop/logo512.png";

const Component: React.FC<ICard> = ({
  data,
  paperProps = {},
  onClickHandler = () => null,
  vatExcluded = false,
}): JSX.Element => {
  const { formatMessage: t, formatNumber: n, formatDate: d } = useIntl();
  const {
    picture,
    modell,
    rate,
    preis,
    fzarts,
    aufbaubereiche,
    wltp_co2klasse,
    wltp_co2_emissionen,
    wltp_kombiniert,
    wltp_stromverbrauch_kombiniert,
  } = data;
  const title = `${modell.marke.name} - ${modell.name}`;
  const EnVKV = new Map();
  if (
    wltp_co2klasse &&
    aufbaubereiche.length === 1 &&
    aufbaubereiche[0].id === 7
  )
    EnVKV.set("wltp_co2klasse", {
      label: t({ id: "wltp_co2klasse" }),
      value: wltp_co2klasse,
    });
  if (wltp_co2_emissionen)
    EnVKV.set("wltp_co2_emissionen", {
      label: t({ id: "wltp_co2_emissionen" }),
      value: wltp_co2_emissionen,
      unit: " g/km",
    });
  if (wltp_kombiniert)
    EnVKV.set("wltp_kombiniert", {
      label: t({ id: "wltp_kombiniert" }),
      value: wltp_kombiniert,
      unit: " l/100km",
    });
  if (wltp_stromverbrauch_kombiniert)
    EnVKV.set("wltp_stromverbrauch_kombiniert", {
      label: t({ id: "wltp_stromverbrauch_kombiniert" }),
      value: wltp_stromverbrauch_kombiniert,
      unit: " kWh/100km",
    });

  const handleImageError = (e: any) => {
    e.target.onerror = null;
    // e.target.style.display = 'none'
    e.target.src = `${process.env.REACT_APP_CDN_SERVER}/default/empty.png`;
  };

  const TextWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
  });
  const vatFactor = vatExcluded ? 1.19 : 1;
  const preisTitle = vatExcluded
    ? "netto zuzüglich MwSt., nur für Gewerbekunden"
    : "inkl. MwSt.";

  return (
    <Card className={"modelle"} square sx={{ boxShadow: 3 }}>
      <CardActionArea
        onClick={onClickHandler}
        disableRipple
        sx={{
          height: "100%",
          display: "grid",
          justifyContent: "space-between",
        }}
      >
        <Stack
          sx={{ justifyContent: "space-between", height: "100%" }}
          direction="column"
        >
          <Box>
            <CardMedia
              loading="lazy"
              component="img"
              image={picture() || defaultPicture}
              onError={handleImageError}
              sx={{
                objectPosition: "top center",
                width: "100%",
                aspectRatio: "12/9",
              }}
              {...{
                title,
                alt: t(
                  {
                    id: "pages.modelle.card.image.alt",
                    defaultMessage: "Foto eines der Fahrzeuge {marke} {modell}",
                  },
                  { marke: modell.marke.name, modell: modell.name },
                ),
              }}
            />
            <div className="image-overlay" />
          </Box>
          <CardHeader
            {...{ title, alt: title }}
            titleTypographyProps={{ variant: "h5", color: "primary" }}
            subheaderTypographyProps={{
              variant: "body1",
              color: "primary",
              sx: { textOverflow: "ellipsis" },
            }}
          />
          <CardContent sx={{ py: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ fontWeight: "bold", flexWrap: "wrap" }}
              useFlexGap
            >
              {fzarts.map(({ id, count }: { id: number; count: number }) => (
                <Chip
                  key={id}
                  label={t({ id: `fzart.${id}.plural` }, { count })}
                />
              ))}
            </Stack>
          </CardContent>
          <CardContent>
            <Typography color="primary" component="span" variant="body2">
              {stringify(EnVKV, " ● ")}
            </Typography>
          </CardContent>
          <CardContent>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <TextWrapper>
                <Box>
                  <Typography color="primary" component="span" variant="body2">
                    ab&nbsp;
                  </Typography>
                  <Typography
                    component="span"
                    color="primary"
                    variant="h5"
                    sx={{ fontWeight: "900" }}
                  >
                    {n(preis / vatFactor, {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </Typography>
                </Box>
                {preisTitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    ({preisTitle})
                  </Typography>
                )}
              </TextWrapper>
              {rate && (
                <Box>
                  <Typography color="primary" component="span" variant="body2">
                    ab&nbsp;
                  </Typography>
                  <Typography
                    component="span"
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: "900" }}
                  >
                    {n(rate, {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    /m.
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default Component;
